<template lang="pug">
v-dialog(v-model="dialog" width="560" persistent)
  v-card
    v-card-title {{ editId ? 'Editar producto o servicio' : 'Nuevo producto o servicio' }}
    v-card-subtitle Con su línea de negocio, ticket promedio y costo variable.
    v-form(ref="form" @submit.prevent="onSubmit")
      v-card-text
        v-combobox(
          v-model="form.line_of_business"
          :items="lineNames"
          :rules="editId ? [] : required"
          :disabled="lockLine"
          label="Línea de negocio"
          hint="Elige una existente o escribe una nueva"
          persistent-hint
          outlined
          dense
        )
        v-text-field.mt-3(
          v-model="form.name"
          :rules="required"
          label="Nombre del producto o servicio"
          outlined
          dense
        )
        v-row(dense)
          v-col(cols="12" sm="6")
            NumberMaskedInput(
              v-model="form.market_price"
              label="Ticket promedio"
              :rules="required"
              outlined
              dense
              type="number"
            )
          v-col(cols="12" sm="6")
            NumberMaskedInput(
              v-model="form.unit_cost"
              label="Costo variable promedio"
              :rules="required"
              outlined
              dense
              type="number"
            )
        NumberMaskedInput.mt-3(
          v-model="form.monthly_production_capacity"
          label="Capacidad máxima al mes (opcional)"
          hint="Cuántos puedes entregar por mes (horas, visitas, proyectos). Déjalo vacío si no tienes tope."
          persistent-hint
          outlined
          dense
          type="number"
        )
        v-alert.mt-4.mb-0(v-if="marginText" dense text :type="marginType")
          | Margen de contribución: {{ marginText }}
      v-card-actions
        v-spacer
        v-btn(text @click="close" :disabled="loading") Cancelar
        v-btn(color="primary" depressed type="submit" :loading="loading") Guardar
</template>

<script>
import { mapActions } from 'vuex'
import NumberMaskedInput from '@/components/common/NumberMaskedInput.vue'

// [campo, % por defecto] — los defaults del modelo (suman 100).
const MONTH_FIELDS = [
  ['january_production_percentage', 8],
  ['february_production_percentage', 8],
  ['march_production_percentage', 8],
  ['april_production_percentage', 8],
  ['may_production_percentage', 8],
  ['june_production_percentage', 8],
  ['july_production_percentage', 8],
  ['august_production_percentage', 8],
  ['september_production_percentage', 9],
  ['october_production_percentage', 9],
  ['november_production_percentage', 9],
  ['december_production_percentage', 9],
]

export default {
  name: 'SimpleProductFormModal',
  components: { NumberMaskedInput },
  data() {
    return {
      dialog: false,
      loading: false,
      lockLine: false,
      editId: null,
      editItem: null,
      lineNames: [],
      form: { line_of_business: null, name: null, market_price: null, unit_cost: null, monthly_production_capacity: null },
      required: [v => (v !== null && v !== undefined && v !== '') || 'Requerido'],
    }
  },
  computed: {
    margin() {
      const p = Number(this.form.market_price)
      const c = Number(this.form.unit_cost)
      if (!p || isNaN(p) || isNaN(c)) return null
      return { amount: p - c, pct: Math.round(((p - c) / p) * 100) }
    },
    marginText() {
      if (!this.margin) return ''
      return `$${this.margin.amount.toLocaleString('es-CL')} (${this.margin.pct}%)`
    },
    marginType() {
      if (!this.margin) return 'info'
      return this.margin.amount > 0 ? 'success' : 'warning'
    },
    // El backend interpreta 0 como "sin tope de capacidad" y omite la restricción
    // en el Mix Óptimo, así que un campo vacío se manda como 0.
    capacityToSend() {
      const cap = Number(this.form.monthly_production_capacity)
      return (!cap || isNaN(cap) || cap < 0) ? 0 : Math.round(cap)
    },
    // La capacidad es read-only en el PATCH del producto: se escribe por el
    // endpoint update-production-capacity, que exige los 12 % de estacionalidad.
    // El modo simplificado no los administra, así que se reenvían tal cual están.
    capacityPayload() {
      const item = this.editItem || {}
      const payload = { monthly_production_capacity: this.capacityToSend }
      MONTH_FIELDS.forEach(([field, fallback]) => {
        const current = Number(item[field])
        payload[field] = isNaN(current) ? fallback : current
      })
      return payload
    },
  },
  methods: {
    ...mapActions('product', ['createSimpleProduct', 'editProduct', 'listLineOfBusiness', 'updateProductProductionCapacity']),
    async loadLines() {
      const res = await this.listLineOfBusiness()
      const d = res && res.data
      const arr = Array.isArray(d) ? d : (d && d.results) || []
      this.lineNames = arr.map(l => l.name).filter(Boolean)
    },
    // Abrir el formulario. opts.line = línea preseleccionada (y bloqueada);
    // opts.item = producto a editar (precarga nombre/ticket/costo).
    open(opts = {}) {
      const { line = null, item = null } = opts
      this.editId = item ? item.id : null
      this.editItem = item || null
      this.lockLine = !!line || !!item
      // En BD, capacidad 0 significa "sin tope declarado": en el formulario se
      // muestra vacío, no como un 0 que parezca "no puedo entregar nada".
      const capacity = item && item.monthly_production_capacity
      this.form = {
        line_of_business: line || (item && (item.lineName || item.line_name)) || null,
        name: item ? item.name : null,
        market_price: item ? item.market_price : null,
        unit_cost: item ? item.unit_cost : null,
        monthly_production_capacity: capacity > 0 ? capacity : null,
      }
      this.loadLines()
      this.dialog = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation())
    },
    close() {
      this.dialog = false
      this.$refs.form && this.$refs.form.resetValidation()
    },
    async onSubmit() {
      if (!this.$refs.form.validate()) return
      this.loading = true
      let res
      if (this.editId) {
        res = await this.editProduct({ id: this.editId, data: {
          name: this.form.name,
          market_price: this.form.market_price,
          unit_cost: this.form.unit_cost,
        } })
        // La capacidad va por su propio endpoint (ver capacityPayload). Guardarla
        // recalcula el Mix Óptimo de los casos que usan este producto.
        if (res && !res.error) {
          const capRes = await this.updateProductProductionCapacity({
            id: this.editId, data: this.capacityPayload })
          if (capRes && capRes.error) {
            this.loading = false
            this.$toast.error('Se guardaron los datos, pero no se pudo guardar la capacidad.')
            return
          }
        }
      } else {
        res = await this.createSimpleProduct({ data: {
          line_of_business: this.form.line_of_business,
          name: this.form.name,
          market_price: this.form.market_price,
          unit_cost: this.form.unit_cost,
          monthly_production_capacity: this.capacityToSend,
        } })
      }
      this.loading = false
      const ok = res && ((res.status >= 200 && res.status < 300) || res.data)
      if (ok && !res.error) {
        this.$toast.success(this.editId ? 'Actualizado.' : 'Producto o servicio creado.')
        this.$emit('save', res.data)
        this.close()
      } else {
        const msg = (res && res.data && res.data.detail) || 'No se pudo guardar. Revisa los datos.'
        this.$toast.error(msg)
      }
    },
  },
}
</script>
